import { useCallback } from 'react';
import { HighnoteTransactionEvent } from 'API';
import { useUserRole } from 'app/hooks/useUserRole';
import { useAppSelector } from 'app/store';
import { selectCurrentPaidolUser, selectUserCompanies } from 'app/store/userCompaniesSlice';

/**
 * Whether this group admin may mark a transaction reviewed: they must manage a
 * group that owns it, and satisfy that group's reviewers if it names any. Per
 * owning group — one naming reviewers contributes only them, one naming none
 * contributes all of its admins, and either is enough.
 */
export const useCanReviewGroupTransaction = () => {
  const currentPaidolUser = useAppSelector(selectCurrentPaidolUser);
  const { isGroupAdmin } = useUserRole();
  const { selectedPaidol } = useAppSelector(selectUserCompanies);
  // The setting the server narrows on. hasReviewers is reported whatever its
  // value, so without this a paidol that turned group approvals back off would
  // hide the button from admins the server still allows. Ownership is checked
  // either way — that scope is not part of this feature.
  const narrowsToReviewers = selectedPaidol?.approvalWorkflowSettings?.groupApprovalsEnabled === true;

  return useCallback(
    (transaction: HighnoteTransactionEvent | null | undefined): boolean => {
      // Narrows the group admin role and nothing else, matching the server, which
      // returns before this rule for any other role. Read from the role rather than
      // inferred from empty links, which is another repo's guarantee to keep.
      if (!isGroupAdmin) return true;

      const cardLinks = currentPaidolUser?.managedGroups?.items ?? [];
      const projectLinks = currentPaidolUser?.managedProjectsGroups?.items ?? [];
      if (cardLinks.length === 0 && projectLinks.length === 0) return true;

      const owningCardGroupId = transaction?.card?.cardGroupId;
      const owningProjectGroupIds = transaction?.owningProjectGroupIds ?? [];
      // Backs up owningProjectGroupIds on a row that predates it, with the same
      // plain membership test the scope authorization runs. procoreProjectId is a
      // number while groupProjects are strings.
      const codedProjectId =
        transaction?.procoreProjectId != null
          ? String(transaction.procoreProjectId)
          : transaction?.agaveProjectId ?? transaction?.flexCodeId ?? null;

      const owning = [
        ...cardLinks
          .filter((item) => !!item?.cardGroupId && item.cardGroupId === owningCardGroupId)
          .map((item) => ({
            namesReviewers: item?.cardGroup?.hasReviewers === true,
            isReviewer: item?.isReviewer === true,
          })),
        ...projectLinks
          .filter(
            (item) =>
              !!item?.projectGroupId &&
              (owningProjectGroupIds.includes(item.projectGroupId) ||
                (!!codedProjectId && (item?.projectGroup?.groupProjects ?? []).includes(codedProjectId)))
          )
          .map((item) => ({
            namesReviewers: item?.projectGroup?.hasReviewers === true,
            isReviewer: item?.isReviewer === true,
          })),
      ];

      if (owning.length > 0) {
        if (!narrowsToReviewers) return true;
        return owning.some((link) => (link.namesReviewers ? link.isReviewer : true));
      }

      return false;
    },
    [currentPaidolUser, isGroupAdmin, narrowsToReviewers]
  );
};
